import { apiGetSupplier, apiGetGames, apiGetSearchGames, apiGetMore } from '@/api';
import * as types from '../mutation-types';

import axios from '@/axios.config';
let oldRequestToken = null;

// Models
import { SUPPLIER } from '@/store/models/supplier';
import PAGINATION from '@/store/models/pagination';

// Services
import { mapEnabled} from '@/store/services/mapEnabled';
import { mapEnabledToSlug } from '@/store/services/mapEnabledToSlug';
import { setEnabled } from '@/store/services/setEnabled';

// Utilities
import { cleanSource } from '@/utilities';

const defaultState = () => {
  return {
    profile: SUPPLIER,

    gamesFilters: {
      order: 'publish_date:desc',
      gameTypes: [],
      themes: [],
      categories: [],
      view: 'card-sm'
    },

    gamesSearch: {
      data: [],
      meta: PAGINATION,
      loading: null
    },

    games: {
      data: [],
      meta: PAGINATION,
      loading: null
    }
  }
}

// initial state
const state = defaultState();

// mutations
const mutations = {
  [types.RECEIVED_SUPPLIER] (state, supplier) {
    state.profile = supplier;
  },

  [types.RECEIVED_SUPPLIER_GAMES] (state, games) {
    state.games.data = games.data;
    state.games.meta = games.meta;
  },

  [types.APPEND_SUPPLIER_GAMES] (state, games) {;
    // state.games.data = state.games.data.concat(games.data);
    state.games.data.push(...games.data);
    state.games.meta = games.meta;
  },

  [types.RECEIVED_SUPPLIER_GAMES_GAMETYPES] (state, gameTypes) {
    state.gamesFilters.gameTypes = gameTypes;
  },

  [types.UPDATE_SUPPLIER_GAMES_GAMETYPES] (state, gameType) {
    // find matching object with slug and update enabled state
    setEnabled(state.gamesFilters.gameTypes, gameType);
  },

  [types.UPDATE_SUPPLIER_GAME_THEMES] (state, theme) {
    // find matching object with slug and update enabled state
    setEnabled(state.gamesFilters.themes, theme);
  },

  [types.UPDATE_SUPPLIER_GAME_CATEGORIES] (state, category) {
    // find matching object with slug and update enabled state
    setEnabled(state.gamesFilters.categories, category);
  },

  [types.UPDATE_SUPPLIER_GAMES_ORDER] (state, order) {
    state.gamesFilters.order = order;
  },

  [types.DISABLE_SUPPLIER_GAMES_FILTERS] (state) {
    state.gamesFilters.gameTypes.forEach(el => el.enabled = false);
    state.gamesFilters.themes.forEach(el => el.enabled = false);
    state.gamesFilters.categories.forEach(el => el.enabled = false);
  },

  [types.RECEIVED_SUPPLIER_GAME_THEMES] (state, themes) {
    state.gamesFilters.themes = themes;
  },

  [types.RECEIVED_SUPPLIER_GAME_CATEGORIES] (state, categories) {
    state.gamesFilters.categories = categories;
  },

  [types.RECEIVED_SUPPLIER_SEARCH_GAMES] (state, data) {
    state.gamesSearch.data = data.data;
    state.gamesSearch.meta = data.meta;
  },

  [types.APPEND_SUPPLIER_SEARCH_GAMES] (state, games) {
    state.gamesSearch.data.push(...games.data);
    state.gamesSearch.meta = games.meta;
  },

  [types.CLEAR_SUPPLIER_SEARCH_GAMES] (state) {
    state.gamesSearch.data = [];
    state.gamesSearch.meta = PAGINATION;
  },

  [types.SET_SUPPLIER_GAMES_VIEW] (state, view) {
    state.gamesFilters.view = view;
  },

  [types.RESET_SUPPLIER] (state) {
    Object.assign(state, defaultState());
  },
};

const actions = {
  getSupplier: ({ commit }, slug) => {
    return apiGetSupplier(slug, { include: 'casinosSummary,stats,featuredGames.stats,featuredGames.suggestedCasinos,featuredGames.suggestedCasinos.casinoGameStats' })
      .then(({ data }) => commit(types.RECEIVED_SUPPLIER, data))
      .catch((error) => {
        console.log(error);
      });
  },

  getSupplierGames: ({ commit }, { slug, args = {}}) => {
    // Argument defaults
    args.limit = args.limit || 16;

    // Because we are within the store we need to
    // use Vue.prototype to access the $mq plugin
    if (Vue.prototype.$mq('sm-max')) { 
      args.is_mobile_friendly = true; 
    };

    let order = state.gamesFilters.order;

    // map game type gamesFilters
    let game_types = mapEnabledToSlug(state.gamesFilters.gameTypes);
    let game_themes = mapEnabledToSlug(state.gamesFilters.themes);
    let game_categories = mapEnabledToSlug(state.gamesFilters.categories);

    // Setup a cancel token so we can cancel any unresolved previous requests
    let token = axios.CancelToken;
    let request = token.source();

    if (oldRequestToken) {
      oldRequestToken.cancel();
      oldRequestToken = null;
    }

    oldRequestToken = request;

    return apiGetGames(
        { 
          order, 
          game_types, 
          game_themes, 
          game_categories, 
          suppliers: [{ slug: slug }],
          include: 'stats,suggestedCasinos.casinoGameStats',
          ...args
        }, 
        request.token
      )
      .then(({ data }) => { 
        commit(types.RECEIVED_SUPPLIER_GAMES, data);
        oldRequestToken = null;
      })
      .catch((error) => {
        console.log(error);
      });
  },

  getMoreSupplierGames: ({ commit }, params) => {
    return apiGetMore(params.url)
      .then(({ data }) => {
        commit(types[params.commit], data);
      })
      .catch((error) => {
        console.log(error);
      });
  },

  updateSupplierGamesOrder: ({ commit, dispatch }, order) => {
    commit(types.UPDATE_SUPPLIER_GAMES_ORDER, order);

    dispatch('getSupplierGames', { slug: state.profile.slug });
  },

  toggleSupplierGamesGameTypeFilter: ({ commit, dispatch }, payload) => {
    if (Array.isArray(payload)) {
      payload.forEach((gametype) => {
        commit(types.UPDATE_SUPPLIER_GAMES_GAMETYPES, gametype);
      });
    } else {
      commit(types.UPDATE_SUPPLIER_GAMES_GAMETYPES, payload);
    }

    dispatch('getSupplierGames', { slug: state.profile.slug });
  },

  toggleSupplierGamesThemeFilter: ({ commit, dispatch }, theme) => {
    commit(types.UPDATE_SUPPLIER_GAME_THEMES, theme);

    dispatch('getSupplierGames', { slug: state.profile.slug });
  },

  toggleSupplierGamesCategoryFilter: ({ commit, dispatch }, category) => {
    commit(types.UPDATE_SUPPLIER_GAME_CATEGORIES, category);

    dispatch('getSupplierGames', { slug: state.profile.slug });
  },

  disableAllSupplierGamesFilters: ({ commit, dispatch }) => {
    commit(types.DISABLE_SUPPLIER_GAMES_FILTERS);
    dispatch('addToast', { type: 'success', text: 'All game gamesFilters have been removed.' })

    dispatch('getSupplierGames', { slug: state.profile.slug });
  },

  searchSupplierGames: ({ commit }, { slug, term, args = {} }) => {
    // Argument defaults
    args.limit = args.limit || 16;

    // Because we are within the store we need to
    // use Vue.prototype to access the $mq plugin
    if (Vue.prototype.$mq('sm-max')) { 
      args.is_mobile_friendly = true; 
    };

    // Setup a cancel token so we can cancel any unresolved previous searches
    let token = axios.CancelToken;
    let request = token.source();

    if (oldRequestToken) {
      oldRequestToken.cancel();
      oldRequestToken = null;
    }

    oldRequestToken = request;

    return apiGetSearchGames(term, request.token, {
        suppliers: [{ slug: slug }],
        include: 'stats,suggestedCasinos.casinoGameStats',
        order: 'title:asc'
      })
      .then(({ data }) => {
        commit(types.RECEIVED_SUPPLIER_SEARCH_GAMES, data);
        oldRequestToken = null;
      })
      .catch((error) => {
        console.log(error);
      });
  },

  clearSearchSupplierGames: ({ commit }) => {
    commit(types.CLEAR_SUPPLIER_SEARCH_GAMES);
  },

  setSupplierGamesView: ({ commit }, view) => {
    commit(types.SET_SUPPLIER_GAMES_VIEW, view);
  }
};

const getters = {
  supplier (state) {
    return state.profile;
  },

  supplierGames (state) {
    return state.games;
  },

  supplierGamesSearch (state) {
    return state.gamesSearch;
  },

  supplierGamesFilters (state) {
    return state.gamesFilters;
  },

  supplierGamesFilterCount: (state, getters) => (filterName) => {
    // Returns the number of enabled filters for the provided 'filterName'
    return state.gamesFilters[filterName].filter(function(item) { 
      return item.enabled;
    }).length;
  },

  supplierGamesAllFiltersCount: (state) => {
    let all = state.gamesFilters.gameTypes.concat(state.gamesFilters.themes, state.gamesFilters.categories);

    return all.filter(function(item) { 
      return item.enabled;
    }).length;
  },

  supplierGamesThemesMostPopular (state) {
    // Remove reactivity so we don't mutate the source
    let items = cleanSource(state.gamesFilters.themes); 

    // Sort based on number of plays, highest will be placed first in the array
    items.sort(function (a, b) {
      return b.themeStats.number_of_plays - a.themeStats.number_of_plays;
    });

    // Return the top 8 most popular themes
    return items.slice(0,8);
  }
};

export default {
  state,
  mutations,
  actions,
  getters
};



// WEBPACK FOOTER //
// ./resources/assets/js/app/store/modules/supplier.js